import { applySuggestion, autoGroup } from "@/input/utils/editOperators";
import { groupSelection, canGroupSelection } from "@/input/utils/groupUngroup";
import { insertLabel } from "@/input/utils/labels";
import { cursorIsActive } from "@/models/CursorModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { OperatorType } from "@/models/nodes/operators";
import { getSymbolContainingNode, hasNodeChildSymbolNode } from "@/models/nodes/utils";
import { isSupSubNode } from "@/models/utils/semanticChecks";

import type { DocumentModel } from "@/models/DocumentModel";
import type { SubSupOperatorType, SupSubOperator } from "@/models/nodes/operators/types";
import type { NodeModel } from "@/models/nodes/types";

export const inputSubSup = (position: "top" | "bottom") => (doc: DocumentModel) => {
  doc.transact(() => {
    const { cursor, input, selection } = doc;

    const operatorType = position === "top" ? OperatorType.POWER : OperatorType.SUBSCRIPT;

    autoGroup(doc, {
      nodeBefore: { hasType: ["operator:fraction"] },
    });

    if (canGroupSelection(doc)) {
      const { activeNode } = selection;

      if (hasNodeChildSymbolNode(activeNode) || activeNode?.is(GroupNodeModel)) {
        if (activeNode.parent?.is(InlineNodeModel)) {
          cursor.moveTo(activeNode.parent, activeNode.index + 1);
          selection.resetSelection();
        }
      } else {
        const group = groupSelection(doc, "(", ")");
        if (group) {
          doc.cursor.moveAfterNode(group);
        }
        doc.editor.resetInput();
      }
    }

    const node = input.editingNode || cursor.nodeBeforeCursor;

    if (!node || !cursorIsActive(cursor)) {
      doc.editor.resetInput();
      return;
    }

    const nodeWithSymbol = getSymbolContainingNode(node);

    if (nodeWithSymbol && (input.value.length > 0 || position === "bottom")) {
      const labelPosition = `right-${position}` as const;
      doc.editor.resetInput(true);
      insertLabel(nodeWithSymbol, labelPosition);
    } else {
      insertSubSup(node, operatorType);
    }

    doc.editor.resetInput();
  }, "operator:sup");
};

function getSubSup(node: NodeModel, operatorType: SubSupOperatorType): SupSubOperator | undefined {
  if (isSupSubNode(node)) {
    if (node.operatorType === operatorType) {
      return node;
    }

    if (isSupSubNode(node.prevSiblings[0])) {
      return node.prevSiblings[0];
    }
  }

  if (isSupSubNode(node.nextSiblings[0])) {
    if (node.nextSiblings[0].operatorType === operatorType) {
      return node.nextSiblings[0];
    }

    if (isSupSubNode(node.nextSiblings[1])) {
      return node.nextSiblings[1];
    }
  }

  return undefined;
}

/**
 * Inserts power or subscript operator. If the operator already exists it moves the cursor to the end of operator's inline node.
 * @param node
 * @param operatorType
 */
function insertSubSup(node: NodeModel, operatorType: SubSupOperatorType) {
  const { doc } = node;
  const { cursor } = doc;

  if (!cursorIsActive(cursor)) {
    return;
  }

  const subSup = getSubSup(node, operatorType);

  if (subSup) {
    cursor.moveTo(subSup.parameters[0], subSup.parameters[0]?.nodes.length);
    return;
  }

  applySuggestion(cursor, doc.create(OperatorNodeModel, operatorType), {
    cursorPlacement: 0,
  });
}
